<!--
  Typically used within a Form to trigger the onSubmit handler.
-->
<script setup lang="ts">
import type { ButtonHTMLAttributes } from 'vue'
import { preserveWidthWhile } from '@shared/composables'
import { useForm } from '@consumer/services/form'
import { loadingIconSizeForButton } from '@consumer/logic/icons'
import type { ButtonSize } from './GButton.vue'

const {
  label = 'Save',
  loading,
  disabled,
  type = 'submit',
  size: explicitSize,
  flat,
  variant,
  primary,
  secondary,
  tertiary,
  pill,
} = defineProps<{
  // The preferred way to specify the content when it's a String.
  label?: string

  // When loading, the button text is replaced with a loading indicator.
  loading?: boolean

  // Whether the button is disabled.
  disabled?: boolean

  // The size of the input.
  size?: ButtonSize

  // By default the button triggers a submit event when used in Form.
  type?: ButtonHTMLAttributes['type']

  // Whether it's a tertiary button.
  tertiary?: boolean

  // Whether it's a secondary button.
  secondary?: boolean

  // Whether it's a primary button.
  primary?: boolean

  // Whether it's a flat button.
  flat?: boolean

  // Whether it's a pill button.
  pill?: boolean

  // Button variant - primary, secondary, tertiary, gold, transparent, flat
  variant?: 'primary' | 'secondary' | 'tertiary' | 'gold' | 'transparent' | 'flat'

  // Whether it's a gold button.
  color?: 'gold'
}>()

const form = useForm()

const isSaving = $computed(() => loading || form?.submitting.value)

// Inputs can inherit their size from the parent form.
const size = $computed(() => explicitSize || (form?.size) || 'large')

// Preserve button width while displaying loading indicator.
const buttonRef = ref()
preserveWidthWhile(buttonRef, () => isSaving)

defineExpose({
  focus: () => buttonRef.value?.focus(),
  blur: () => buttonRef.value?.blur(),
  click: () => buttonRef.value?.click(),
})
</script>

<template>
  <GButton
    ref="buttonRef"
    class="save-button"
    :primary="primary || (!tertiary && !secondary && !variant)"
    :tertiary="tertiary"
    :secondary="secondary"
    :flat="flat"
    :variant="variant"
    :color="color"
    :disabled="disabled || isSaving || form?.disableSubmit.value"
    :pill="pill"
    :size="size"
    :type="type"
    :label="label"
  >
    <LoadingIndicator v-if="isSaving" :size="loadingIconSizeForButton(size)"/>
    <slot v-else>{{ label }}</slot>
  </GButton>
</template>
